import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, MailOpen, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const ContactInbox = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getContacts(token);
      if (response.success) {
        setContacts(response.data.contacts);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load contacts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (contactId) => {
    try {
      await adminAPI.updateContactStatus(contactId, 'read', token);
      setContacts(
        contacts.map((c) =>
          c.id === contactId ? { ...c, status: 'read' } : c
        )
      );
      toast({
        title: 'Success',
        description: 'Contact marked as read',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update contact',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await adminAPI.deleteContact(contactId, token);
      setContacts(contacts.filter((c) => c.id !== contactId));
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
      toast({
        title: 'Success',
        description: 'Contact deleted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete contact',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Contact Inbox</h2>
          <p className="text-gray-400">
            {contacts.length} total messages
          </p>
        </div>
        <Button
          onClick={fetchContacts}
          variant="outline"
          className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
        >
          <RefreshCw size={18} className="mr-2" />
          Refresh
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="glass p-12 rounded-3xl border border-orange-500/20 text-center">
          <Mail size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No contacts yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact List */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`glass p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:border-orange-500/50 ${
                  selectedContact?.id === contact.id
                    ? 'border-orange-500/50 bg-orange-500/5'
                    : 'border-orange-500/20'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {contact.status === 'unread' ? (
                      <Mail size={18} className="text-orange-500" />
                    ) : (
                      <MailOpen size={18} className="text-gray-500" />
                    )}
                    <h3 className="font-semibold text-white">{contact.name}</h3>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      contact.status === 'unread'
                        ? 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                        : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                    }
                  >
                    {contact.status}
                  </Badge>
                </div>
                <p className="text-sm text-gray-400 mb-1">{contact.email}</p>
                <p className="text-sm font-medium text-gray-300 mb-2">{contact.subject}</p>
                <p className="text-xs text-gray-500">{formatDate(contact.createdAt)}</p>
              </div>
            ))}
          </div>

          {/* Contact Detail */}
          <div className="glass p-6 rounded-3xl border border-orange-500/20 sticky top-6">
            {selectedContact ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {selectedContact.name}
                    </h3>
                    <a
                      href={`mailto:${selectedContact.email}`}
                      className="text-orange-500 hover:text-orange-400"
                    >
                      {selectedContact.email}
                    </a>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      selectedContact.status === 'unread'
                        ? 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                        : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                    }
                  >
                    {selectedContact.status}
                  </Badge>
                </div>

                <div className="border-t border-orange-500/10 pt-4">
                  <p className="text-sm text-gray-400 mb-2">
                    {formatDate(selectedContact.createdAt)}
                  </p>
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {selectedContact.subject}
                  </h4>
                  <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>

                <div className="flex gap-2 pt-4 border-t border-orange-500/10">
                  {selectedContact.status === 'unread' && (
                    <Button
                      onClick={() => handleMarkAsRead(selectedContact.id)}
                      variant="outline"
                      className="flex-1 border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
                    >
                      <MailOpen size={18} className="mr-2" />
                      Mark as Read
                    </Button>
                  )}
                  <Button
                    onClick={() => handleDelete(selectedContact.id)}
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 size={18} className="mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Select a contact to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactInbox;
